import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [targets, setTargets] = useState([]);
  const [dailyEntries, setDailyEntries] = useState({});
  const [rewards, setRewards] = useState([]);
  const [punishments, setPunishments] = useState([]);
  const [debugMode, setDebugMode] = useState(false);
  const [debugDate, setDebugDate] = useState(new Date().toISOString().split('T')[0]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const loadData = useCallback(async () => {
    const data = await api.getData();
    setTargets(data.targets || []);
    setDailyEntries(data.dailyEntries || {});
    setRewards(data.rewards || []);
    setPunishments(data.punishments || []);
    setLoadError(null);
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setTargets([]);
      setDailyEntries({});
      setRewards([]);
      setPunishments([]);
      setIsInitialized(false);
      return;
    }

    loadData().catch((err) => {
      setLoadError(err.message || 'Failed to load data');
      setIsInitialized(true);
    });
  }, [isAuthenticated, loadData]);

  const addTarget = async (target) => {
    const result = await api.createTarget(target);
    setTargets(prev => [...prev, result.target]);
  };

  const updateTarget = async (id, updatedTarget) => {
    const result = await api.updateTarget(id, updatedTarget);
    setTargets(prev => prev.map(target =>
      target.id === id ? result.target : target
    ));
  };

  const deleteTarget = async (id) => {
    await api.deleteTarget(id);
    setTargets(prev => prev.filter(target => target.id !== id));
  };

  const addDailyEntry = async (date, targetId, value) => {
    setDailyEntries(prev => ({
      ...prev,
      [date]: {
        ...prev[date],
        [targetId]: value
      }
    }));

    try {
      await api.upsertEntry(date, targetId, value);
    } catch (err) {
      await loadData();
      throw err;
    }
  };

  const getDailyEntry = (date, targetId) => {
    return dailyEntries[date]?.[targetId] || 0;
  };

  const addReward = async (reward) => {
    const result = await api.createReward(reward);
    setRewards(prev => [...prev, result.reward]);
  };

  const addPunishment = async (punishment) => {
    const result = await api.createPunishment(punishment);
    setPunishments(prev => [...prev, result.punishment]);
  };

  const completeReward = async (id) => {
    await api.deleteReward(id);
    setRewards(prev => prev.filter(reward => reward.id !== id));
  };

  const completePunishment = async (id) => {
    await api.deletePunishment(id);
    setPunishments(prev => prev.filter(punishment => punishment.id !== id));
  };

  const getWeekProgress = (targetId, weekStart) => {
    const target = targets.find(t => t.id === targetId);
    if (!target) return { completed: 0, total: 7, percentage: 0 };

    let completed = 0;
    const weekDates = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      weekDates.push(date.toISOString().split('T')[0]);
    }

    weekDates.forEach(date => {
      const entry = getDailyEntry(date, targetId);

      if (target.type === 'daily' && entry >= target.amount) {
        completed++;
      } else if (target.type === 'weekly_frequency' && entry > 0) {
        completed++;
      } else if (target.type === 'weekly_total') {
        completed += entry;
      }
    });

    let total;
    if (target.type === 'weekly_total') {
      total = target.amount;
    } else if (target.type === 'weekly_frequency') {
      total = target.frequency || 7;
    } else {
      total = 7;
    }

    const percentage = total > 0 ? Math.min((completed / total) * 100, 100) : 0;
    return { completed, total, percentage };
  };

  const getCurrentWeekStart = () => {
    const now = debugMode ? new Date(debugDate + 'T00:00:00') : new Date();
    const dayOfWeek = now.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - daysToSubtract);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };

  const getCurrentDate = () => {
    return debugMode ? debugDate : new Date().toISOString().split('T')[0];
  };

  const clearTrackingData = async () => {
    await api.clearTrackingData();
    setDailyEntries({});
    setRewards([]);
    setPunishments([]);
  };

  const removeIncorrectPunishments = () => {
    setPunishments(prev => prev.filter(punishment => {
      const target = targets.find(t => t.id === punishment.targetId);
      if (!target) return false;

      const weekStart = new Date(punishment.weekStart);
      const progress = getWeekProgress(target.id, weekStart);

      let isCompleted = false;
      if (target.type === 'daily') {
        isCompleted = progress.completed >= 7;
      } else if (target.type === 'weekly_frequency') {
        isCompleted = progress.completed >= (target.frequency || 7);
      } else if (target.type === 'weekly_total') {
        isCompleted = progress.completed >= target.amount;
      }

      return !isCompleted;
    }));
  };

  const value = {
    targets,
    dailyEntries,
    rewards,
    punishments,
    debugMode,
    debugDate,
    isInitialized,
    loadError,
    setDebugMode,
    setDebugDate,
    addTarget,
    updateTarget,
    deleteTarget,
    addDailyEntry,
    getDailyEntry,
    addReward,
    addPunishment,
    completeReward,
    completePunishment,
    getWeekProgress,
    getCurrentWeekStart,
    getCurrentDate,
    clearTrackingData,
    removeIncorrectPunishments
  };

  if (!isInitialized && isAuthenticated) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>Loading your data...</h3>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="card">
        <div className="empty-state">
          <h3>Unable to load data</h3>
          <p>{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};
